import React, { useState } from "react";
import {
  Box, Container, Heading, Text, VStack, HStack, SimpleGrid, Button, Icon,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Grid, GridItem, Select, FormControl, FormLabel, Input, Textarea, Alert, AlertIcon,
  Image, Flex,
} from "@chakra-ui/react";
import { FaArrowRight, FaPhone, FaCheckCircle } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const SAND = "#C2A878";
const FINANCE_PRIMARY = "#0f1538";

// Public asset paths
const A = (n: number, ext: string) => `/assets/finance-assets/asset ${n}.${ext}`;

const keyFeatures = [
  { title: "Loan Amounts",                  desc: "From $3,000 up to $80,000 to support your goals." },
  { title: "Flexible Terms",                desc: "Choose repayment periods that suit you from 1 to 7 years." },
  { title: "Quick Access to Funds",         desc: "Receive funds in as little as 24 hours upon finance approval." },
  { title: "Customer-First Approach",       desc: "We work for you, not the banks, with access to over 40 lenders." },
  { title: "Personalised Rates",            desc: "Interest rates tailored specific to your credit profile." },
  { title: "We're With You All the Way",    desc: "Finance can feel overwhelming, but you don't have to do it alone." },
];

const processSteps = [
  {
    num: "/ 01",
    title: "Apply Online",
    desc: "Get started with our easy online application, it takes just 10 minutes. No lengthy questions or paperwork. Just a quick way to help us understand what you need.",
    img: A(5, "jpeg"),
    imgAlt: "Couple applying online for a personal loan",
    showCTA: true,
  },
  {
    num: "/ 02",
    title: "Speak with our Friendly Team",
    desc: "After you apply, one of our team members will reach out for a chat. We'll talk through your goals, answer your questions and guide you through your next steps — no jargon, just real help.",
    img: A(6, "jpeg"),
    imgAlt: "Couple speaking with a finance consultant",
    showCTA: false,
  },
  {
    num: "/ 03",
    title: "Access Funds",
    desc: "Once approved, your funds are on the way, often within 24 hours. We know timing matters, so we work fast to get you moving without delay.",
    img: A(7, "jpeg"),
    imgAlt: "Couple celebrating approved loan",
    showCTA: false,
  },
];

const useCasesLeft = [
  { label: "Debt Consolidation",  sub: "Simplify and take control of your finances" },
  { label: "Weddings",            sub: "Celebrate your big day, your way" },
  { label: "Cars and Motorbikes", sub: "Drive off in your dream vehicle" },
  { label: "Medical",             sub: "Take care of your health and wellbeing" },
  { label: "Home Renovations",    sub: "Turn your house into a home" },
];

const useCasesRight = [
  { label: "Travel",                      sub: "Book that dream holiday you've been waiting for" },
  { label: "Education",                   sub: "Invest in your future with study-related costs" },
  { label: "Moving Costs",                sub: "Make relocating smoother" },
  { label: "Assets and Equipment",        sub: "Fund tools and gear to support your goals" },
  { label: "Marketing and Digital Services", sub: "Web design, branding, SEO, advertising and content creation" },
];

const partnerBenefits = [
  "Offer finance without needing a credit licence",
  "Easy online application your customers can complete in minutes",
  "Increase conversion and average transaction values",
  "Earn commission on every funded application",
  "Dedicated support from our team",
  "No merchant fees",
];

const faqs = [
  {
    q: "What can I use a personal loan for?",
    a: "You can use a personal loan for all kinds of life needs, from home renovations, travel and buying a car, to moving expenses, debt consolidation, weddings and medical bills. Something else in mind? Let's chat — chances are we can help!",
  },
  {
    q: "How much can I borrow?",
    a: "We offer personal loans ranging from $3,000 to $80,000. Your borrowing limit can be affected by your credit score and disposable income.",
  },
  {
    q: "What are the eligibility criteria?",
    a: "To qualify for a personal loan, you must: be aged 18 or over, be an Australian citizen or permanent resident, earn over $25,000 annually from a verifiable, regular income source, and maintain a good credit history. Had a credit hiccup in the past? Don't stress — give us a call. Everyone's situation is different and we're here to talk through your options without judgement.",
  },
  {
    q: "How do I apply?",
    a: "Applying is straightforward. Complete our quick online application in just 2–3 minutes — no lengthy questions and no credit impact. Securely upload your documents from any device and our smart technology instantly matches you with your best loan rate. Once approved, same-day processing means you can receive your funds promptly.",
  },
  {
    q: "Are there any fees?",
    a: "We offer competitive rates and no early repayment fees, making it easier to pay off your loan sooner without penalties. Application fees may apply depending on your loan amount and term.",
  },
  {
    q: "Can I repay my loan early?",
    a: "Yes, you can make early or extra repayments without additional costs. This could help you save on the interest you pay over the life of the loan.",
  },
  {
    q: "How is my interest rate determined?",
    a: "Your interest rate is personalised based on your credit history and financial situation. If you have demonstrated good credit behaviour, you may be offered a lower interest rate.",
  },
];

const loanOptions = [
  {
    title: "Car",
    desc: "Are you looking for vehicle finance? Whether you need finance for personal or business use, we're here to get you the best loan rates available amongst our lenders.",
    img: A(19, "jpeg"),
    imgAlt: "Sports car on open road",
    to: "/finance/services/personal-asset",
  },
  {
    title: "Caravan",
    desc: "Looking to buy a caravan for your next adventure with the family? We work with multiple lenders and can find you the best deals on new and used caravan finance.",
    img: A(20, "jpeg"),
    imgAlt: "4WD towing caravan in Australian outback",
    to: "/finance/services/personal-asset",
  },
  {
    title: "Jet Ski",
    desc: "Do you enjoy the sun and sand? We have flexible and affordable new and used jet ski finance options to suit any budget. We work for you to get you the best finance for your recreational activity.",
    img: A(21, "jpeg"),
    imgAlt: "Man riding jet ski on water",
    to: "/finance/services/personal-asset",
  },
  {
    title: "Equipment",
    desc: "Whether you are looking to finance new or used commercial vehicles, machinery, or plant assets, we've got a range of finance solutions to suit your needs and budget.",
    img: A(22, "jpeg"),
    imgAlt: "Construction excavators on a work site",
    to: "/finance/services/personal-asset",
  },
];

const FinanceServicesPersonalLoansV2: React.FC = () => {
  const [formStatus, setFormStatus] = useState<"idle" | "success">("idle");
  const [financeType, setFinanceType] = useState("");

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus("success");
  };

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/finance/services/personal-loans-v2"
        fallbackTitle="Personal Finance – Flexible Personal Loans | One Finance Australia"
        fallbackDescription="Tailored personal loans from $3,000 to $80,000. Flexible repayment terms, personalised rates, and fast approvals. Apply online in 10 minutes."
      />
      <NavbarWithCallToAction />

      {/* ── Hero ── */}
      <Box
        position="relative"
        minH={{ base: "560px", lg: "640px" }}
        overflow="hidden"
        bg={NAVY}
        color="white"
      >
        {/* Right: warm lifestyle image — chevron points left toward buttons */}
        <Box
          position="absolute"
          right={0}
          top={0}
          bottom={0}
          w={{ base: "100%", lg: "54%" }}
          overflow="hidden"
          style={{ clipPath: "polygon(16% 0, 100% 0, 100% 100%, 16% 100%, 0% 62%)" }}
        >
          <Box
            as="img"
            src={A(13, "jpeg")}
            alt="Couple reviewing personal loan options at home"
            position="absolute"
            inset={0}
            w="full"
            h="full"
            objectFit="cover"
            objectPosition="center"
            display="block"
            style={{
              filter: "sepia(18%) saturate(1.25) brightness(1.06) hue-rotate(-8deg)",
            }}
          />
        </Box>

        {/* Mobile overlay so text stays readable */}
        <Box
          position="absolute"
          inset={0}
          bg="rgba(10,18,50,0.68)"
          display={{ base: "block", lg: "none" }}
        />

        {/* Text content — left-aligned on desktop */}
        <Container maxW="container.xl" position="relative" zIndex={1} h="full">
          <Flex
            h="full"
            minH={{ base: "560px", lg: "640px" }}
            alignItems="center"
            py={{ base: 24, lg: 0 }}
          >
            <VStack
              align={{ base: "center", lg: "flex-start" }}
              textAlign={{ base: "center", lg: "left" }}
              spacing={6}
              maxW={{ base: "full", lg: "48%" }}
            >
              <Text
                fontSize="xs"
                textTransform="uppercase"
                letterSpacing="widest"
                color={SAND}
                fontWeight="700"
              >
                Personal Finance
              </Text>
              <Heading as="h1" size="2xl" fontWeight="bold" lineHeight="1.15">
                Let us Help Bring Your Plans to Life!
              </Heading>
              <Text fontSize="lg" color="whiteAlpha.800" maxW="md">
                Tailored Personal Loans. Flexible solutions to meet your unique needs,
                with competitive rates and swift approvals.
              </Text>
              <HStack spacing={4} pt={2} flexWrap="wrap" justify={{ base: "center", lg: "flex-start" }}>
                <Button
                  as={RouterLink}
                  to="/finance/apply"
                  size="lg"
                  bg={SAND}
                  color={NAVY}
                  fontWeight="700"
                  rightIcon={<FaArrowRight />}
                  _hover={{ opacity: 0.9 }}
                >
                  Apply Now
                </Button>
                <Button
                  as={RouterLink}
                  to="/finance/contact"
                  size="lg"
                  variant="outline"
                  borderColor="white"
                  color="white"
                  _hover={{ bg: "whiteAlpha.200" }}
                >
                  Book a Free Consultation
                </Button>
              </HStack>
            </VStack>
          </Flex>
        </Container>
      </Box>

      {/* ── Partnership / Powered By ── */}
      <Box py={10} bg={NAVY}>
        <Container maxW="container.xl">
          <Flex align="center" justify="center" gap={10} flexWrap="wrap">
            <Text fontSize="sm" color="whiteAlpha.600" textTransform="uppercase" letterSpacing="widest" fontWeight="600">
              Finance powered by
            </Text>
            <Image
              src={A(1, "svg")}
              alt="One Finance Australia partner network"
              h="36px"
              objectFit="contain"
            />
            <Text fontSize="sm" color="whiteAlpha.700" maxW="sm">
              Smart technology matching you against{" "}
              <Text as="span" fontWeight="700" color={SAND}>40+ lenders</Text>{" "}
              for the most competitive rate available for your profile.
            </Text>
          </Flex>
        </Container>
      </Box>

      {/* ── Key Features ── */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={5}>
            {keyFeatures.map((f, i) => (
              <HStack
                key={i}
                align="start"
                p={6}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                spacing={4}
                _hover={{ shadow: "md", borderColor: SAND }}
                transition="all 0.2s"
              >
                <Icon as={FaCheckCircle} color={SAND} boxSize="24px" flexShrink={0} mt="2px" />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="700" color={NAVY}>{f.title}</Text>
                  <Text fontSize="sm" color="gray.600">{f.desc}</Text>
                </VStack>
              </HStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Our Process ── */}
      <Box py={20} bg="white">
        <Container maxW="container.xl">
          <VStack spacing={3} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>Our Process</Heading>
            <Text fontSize="md" color="gray.600" maxW="3xl">
              Have questions and want to talk to someone? We're here to help. Start a quick chat
              with our team, or give us a call on{" "}
              <Text as="span" fontWeight="700" color={SAND}>1300 663 765</Text>. We'll happily
              talk through your situation, answer any questions and help you explore the right
              finance options — no pressure, just guidance.
            </Text>
          </VStack>

          <VStack spacing={6}>
            {processSteps.map((step, i) => (
              <Grid
                key={i}
                templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                w="full"
                overflow="hidden"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="2xl"
                shadow="sm"
              >
                {/* Text side */}
                <GridItem
                  order={{ base: 2, md: i % 2 === 0 ? 1 : 2 }}
                  p={{ base: 8, md: 14 }}
                  display="flex"
                  alignItems="center"
                  bg="white"
                >
                  <VStack align="start" spacing={5}>
                    <Text fontSize="4xl" fontWeight="800" color={SAND} lineHeight={1}>
                      {step.num}
                    </Text>
                    <Heading size="lg" color={NAVY}>{step.title}</Heading>
                    <Text color="gray.600" lineHeight="tall">{step.desc}</Text>
                    {step.showCTA && (
                      <Button
                        as={RouterLink}
                        to="/finance/apply"
                        bg={NAVY}
                        color="white"
                        rightIcon={<FaArrowRight />}
                        _hover={{ bg: FINANCE_PRIMARY }}
                        mt={2}
                      >
                        Apply Now
                      </Button>
                    )}
                  </VStack>
                </GridItem>
                {/* Image side */}
                <GridItem
                  order={{ base: 1, md: i % 2 === 0 ? 2 : 1 }}
                  h={{ base: "260px", md: "400px" }}
                  overflow="hidden"
                >
                  <Box
                    as="img"
                    src={step.img}
                    alt={step.imgAlt}
                    w="full"
                    h="full"
                    objectFit="cover"
                    display="block"
                  />
                </GridItem>
              </Grid>
            ))}
          </VStack>
        </Container>
      </Box>

      {/* ── Support Beyond the Loan ── */}
      <Box py={20} bg={NAVY} color="white" textAlign="center">
        <Container maxW="container.md">
          <VStack spacing={5}>
            <Heading size="xl">Support Beyond the Loan</Heading>
            <Text fontSize="lg" color="gray.300" maxW="xl">
              Have questions later? Need to talk something through? We're still here. Our team is
              just a call or click away whenever you need a little extra support down the track.
            </Text>
            <HStack spacing={3} pt={2}>
              <Icon as={FaPhone} color={SAND} />
              <Text fontWeight="700" color={SAND} fontSize="lg">1300 663 765</Text>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* ── Personal Loans for All Your Life Goals ── */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading size="xl" color={NAVY}>Personal loans for all your life goals.</Heading>
            <Text fontSize="lg" color="gray.600" maxW="2xl">
              From kicking off a fresh start to making memories that last, we're here to help you
              fund what matters most.
            </Text>
            <Button
              as={RouterLink}
              to="/finance/apply"
              size="lg"
              bg={SAND}
              color={NAVY}
              fontWeight="700"
              rightIcon={<FaArrowRight />}
              _hover={{ opacity: 0.9 }}
            >
              Apply Now
            </Button>
          </VStack>

          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="2xl"
            p={{ base: 8, md: 12 }}
            shadow="sm"
          >
            <Text
              textTransform="uppercase"
              fontSize="sm"
              fontWeight="700"
              color={SAND}
              letterSpacing="wider"
              mb={8}
            >
              What can we help you finance?
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={5}>
              <VStack align="start" spacing={5}>
                {useCasesLeft.map((u, i) => (
                  <HStack key={i} align="start" spacing={4}>
                    <Icon as={FaCheckCircle} color={SAND} boxSize="22px" flexShrink={0} mt="2px" />
                    <Box>
                      <Text fontWeight="700" color={NAVY}>{u.label}</Text>
                      <Text fontSize="sm" color="gray.600">{u.sub}</Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
              <VStack align="start" spacing={5}>
                {useCasesRight.map((u, i) => (
                  <HStack key={i} align="start" spacing={4}>
                    <Icon as={FaCheckCircle} color={SAND} boxSize="22px" flexShrink={0} mt="2px" />
                    <Box>
                      <Text fontWeight="700" color={NAVY}>{u.label}</Text>
                      <Text fontSize="sm" color="gray.600">{u.sub}</Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </SimpleGrid>
          </Box>
        </Container>
      </Box>

      {/* ── Partner Program ── */}
      <Box py={20} bg="white">
        <Container maxW="container.xl">
          <Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={16} alignItems="center">
            {/* Image */}
            <Box borderRadius="2xl" overflow="hidden" h={{ base: "280px", md: "440px" }} shadow="lg">
              <Box
                as="img"
                src={A(18, "jpeg")}
                alt="Finance consultant with clients"
                w="full"
                h="full"
                objectFit="cover"
                display="block"
              />
            </Box>

            {/* Content */}
            <VStack align="start" spacing={6}>
              <Text fontSize="sm" textTransform="uppercase" fontWeight="700" color={SAND} letterSpacing="wider">
                Partner Program
              </Text>
              <Heading size="xl" color={NAVY}>The One Finance Partner Program</Heading>
              <Text fontSize="lg" color={SAND} fontWeight="600">
                Grow your business while helping your customers say 'yes' sooner.
              </Text>
              <Text color="gray.600">
                Our Partner Program is designed for businesses who want to offer fast, flexible
                finance solutions without the hassle. Whether you're in retail, health, home
                improvement, automotive, or beyond, we'll work with you to support your clients
                and boost your revenue.
              </Text>
              <VStack align="start" spacing={3} w="full">
                <Text fontWeight="700" color={NAVY}>What's in it for you?</Text>
                {partnerBenefits.map((b, i) => (
                  <HStack key={i} spacing={3} align="start">
                    <Icon as={FaCheckCircle} color={SAND} boxSize="20px" flexShrink={0} mt="2px" />
                    <Text color="gray.700" fontSize="sm">{b}</Text>
                  </HStack>
                ))}
              </VStack>
              <Box p={7} bg={NAVY} borderRadius="xl" color="white" w="full" mt={2}>
                <Heading size="md" mb={3} color={SAND}>Let's grow together.</Heading>
                <Text color="gray.300" mb={5} fontSize="sm">
                  Join the partner program by booking a 30-minute initial consultation, or call{" "}
                  <Text as="span" fontWeight="700" color={SAND}>1300 663 765</Text>.
                </Text>
                <Button
                  as={RouterLink}
                  to="/finance/contact"
                  bg={SAND}
                  color={NAVY}
                  fontWeight="700"
                  rightIcon={<FaArrowRight />}
                  _hover={{ opacity: 0.9 }}
                  w="full"
                >
                  Become a Partner
                </Button>
              </Box>
            </VStack>
          </Grid>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={NAVY}>Frequently Asked Questions</Heading>
          </VStack>
          <Accordion allowMultiple maxW="3xl" mx="auto">
            {faqs.map((faq, i) => (
              <AccordionItem
                key={i}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                mb={3}
                bg="white"
                overflow="hidden"
              >
                <AccordionButton
                  py={5}
                  px={7}
                  _hover={{ bg: "gray.50" }}
                  _expanded={{ bg: NAVY, color: "white" }}
                >
                  <Text flex="1" textAlign="left" fontWeight="600" fontSize="md">
                    {faq.q}
                  </Text>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel px={7} py={5} bg="gray.50">
                  <Text color="gray.700" lineHeight="tall">{faq.a}</Text>
                </AccordionPanel>
              </AccordionItem>
            ))}
          </Accordion>
        </Container>
      </Box>

      {/* ── Still Have Questions / Contact Form ── */}
      <Box py={20} bg="white">
        <Container maxW="container.md">
          <VStack spacing={4} textAlign="center" mb={10}>
            <Heading size="xl" color={NAVY}>Still have questions?</Heading>
            <Text color="gray.600" fontSize="lg" maxW="2xl">
              No worries — finance can feel like a lot, but that's what we're here for. If there's
              anything you're unsure about, or if you just want to chat through your options, our
              friendly team is just a message or call away. No pressure, no jargon, just real
              people here to help.
            </Text>
          </VStack>

          {formStatus === "success" ? (
            <Alert status="success" borderRadius="xl" p={6}>
              <AlertIcon />
              <Text>
                Thank you! Your details have been received and we will be in touch within 1–2
                business days. If it is urgent, you are welcome to call{" "}
                <Text as="span" fontWeight="700">1300 663 765</Text>.
              </Text>
            </Alert>
          ) : (
            <Box
              as="form"
              onSubmit={handleFormSubmit}
              bg="white"
              p={{ base: 8, md: 12 }}
              borderRadius="2xl"
              border="1px solid"
              borderColor="gray.200"
              shadow="sm"
            >
              <VStack spacing={5}>
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Please select your finance type below</FormLabel>
                  <Select
                    placeholder="Select finance type"
                    value={financeType}
                    onChange={(e) => setFinanceType(e.target.value)}
                    borderColor="gray.300"
                    _focus={{ borderColor: SAND, boxShadow: `0 0 0 1px ${SAND}` }}
                  >
                    <option>Car Finance</option>
                    <option>Caravan Finance</option>
                    <option>Jet Ski Finance</option>
                    <option>Equipment Finance</option>
                    <option>First Home Loan</option>
                    <option>Refinancing</option>
                    <option>Investment</option>
                    <option>Personal Finance</option>
                    <option>Debt Consolidation</option>
                    <option>Other</option>
                  </Select>
                </FormControl>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                  <FormControl>
                    <FormLabel color="gray.700" fontWeight="600">First name</FormLabel>
                    <Input placeholder="Jane" borderColor="gray.300" _focus={{ borderColor: SAND, boxShadow: `0 0 0 1px ${SAND}` }} />
                  </FormControl>
                  <FormControl>
                    <FormLabel color="gray.700" fontWeight="600">Last name</FormLabel>
                    <Input placeholder="Smith" borderColor="gray.300" _focus={{ borderColor: SAND, boxShadow: `0 0 0 1px ${SAND}` }} />
                  </FormControl>
                </SimpleGrid>
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Email address</FormLabel>
                  <Input type="email" placeholder="jane@example.com" borderColor="gray.300" _focus={{ borderColor: SAND, boxShadow: `0 0 0 1px ${SAND}` }} />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Phone number</FormLabel>
                  <Input type="tel" placeholder="04XX XXX XXX" borderColor="gray.300" _focus={{ borderColor: SAND, boxShadow: `0 0 0 1px ${SAND}` }} />
                </FormControl>
                <FormControl>
                  <FormLabel color="gray.700" fontWeight="600">Message</FormLabel>
                  <Textarea placeholder="Tell us a bit about what you're looking for…" rows={4} borderColor="gray.300" _focus={{ borderColor: SAND, boxShadow: `0 0 0 1px ${SAND}` }} />
                </FormControl>
                <Button
                  type="submit"
                  size="lg"
                  bg={NAVY}
                  color="white"
                  w="full"
                  fontWeight="700"
                  rightIcon={<FaArrowRight />}
                  _hover={{ bg: FINANCE_PRIMARY }}
                >
                  Send Message
                </Button>
              </VStack>
            </Box>
          )}
        </Container>
      </Box>

      {/* ── Your Loan Options ── */}
      <Box py={20} bg="gray.50">
        <Container maxW="container.xl">
          <VStack spacing={4} textAlign="center" mb={12}>
            <Heading size="xl" color={NAVY}>Your loan options</Heading>
            <Text color="gray.600" fontSize="lg" maxW="2xl">
              In many cases, structuring your loans and finances appropriately is all about asking
              the right questions. See below our most popular client scenarios that best suit your
              current position.
            </Text>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {loanOptions.map((opt, i) => (
              <Box
                key={i}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="xl"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                _hover={{ shadow: "xl", transform: "translateY(-4px)", borderColor: SAND }}
                transition="all 0.3s"
              >
                <Box h="180px" overflow="hidden">
                  <Box
                    as="img"
                    src={opt.img}
                    alt={opt.imgAlt}
                    w="full"
                    h="full"
                    objectFit="cover"
                    display="block"
                  />
                </Box>
                <Box p={6} display="flex" flexDirection="column" flex="1">
                  <Heading size="md" color={NAVY} mb={3}>{opt.title}</Heading>
                  <Text fontSize="sm" color="gray.600" flex="1" mb={5}>{opt.desc}</Text>
                  <Button
                    as={RouterLink}
                    to={opt.to}
                    variant="outline"
                    borderColor={NAVY}
                    color={NAVY}
                    fontWeight="700"
                    size="sm"
                    letterSpacing="wider"
                    textTransform="uppercase"
                    rightIcon={<FaArrowRight />}
                    _hover={{ bg: NAVY, color: "white" }}
                  >
                    Learn More
                  </Button>
                </Box>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* ── Final CTA ── */}
      <Box py={20} bg={NAVY} color="white" textAlign="center">
        <Container maxW="container.md">
          <VStack spacing={5}>
            <Heading size="xl">Ready to take the next step?</Heading>
            <Text fontSize="lg" color="gray.300">
              Take the leap — your goals are closer than you think.
            </Text>
            <Button
              as={RouterLink}
              to="/finance/apply"
              size="lg"
              bg={SAND}
              color={NAVY}
              fontWeight="700"
              rightIcon={<FaArrowRight />}
              _hover={{ opacity: 0.9 }}
              mt={2}
            >
              Book a Free Consultation
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* ── Google Review Banner ── */}
      <Box py={8} bg="white">
        <Container maxW="container.md">
          <Box
            as="img"
            src={A(12, "jpeg")}
            alt="Click here to leave us a review on Google"
            w="full"
            borderRadius="xl"
            cursor="pointer"
            _hover={{ opacity: 0.9 }}
            display="block"
          />
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default FinanceServicesPersonalLoansV2;
